import { useContext, useEffect, useMemo, useRef } from "preact/hooks";
import { findRollableNotations } from "../../utils/dice";
import { CHIP_CLASS, RollContext } from "../ui/roll_chip";

const CHANCE = `${CHIP_CLASS} cs-chance mx-px px-1.5`;
const attr = (text: string) => text.replace(/[&<>"]/g, (c) => `&#${c.charCodeAt(0)};`);

// The chances written in a rule ("1-2 em 1d6") as buttons, outside links, which a button cannot nest in. A rule
// is trusted HTML from Markdown::Odo, and its text is already escaped, so the match goes back in as it came.
export function chancify(html: string, title: (instruction: string, success: string) => string): string {
	let inLink = 0;
	return html
		.split(/(<[^>]+>)/)
		.map((part) => {
			if (part.startsWith("<")) {
				const tag = /^<(\/?)(a|button)\b/i.exec(part);
				if (tag) inLink += tag[1] ? -1 : 1;
				return part;
			}
			if (inLink > 0) return part;
			let out = "";
			let from = 0;
			for (const { text, index, instruction, success } of findRollableNotations(part, { bareChance: true })) {
				if (!success) continue;
				const label = attr(title(instruction, success));
				out += `${part.slice(from, index)}<button type="button" class="${CHANCE}" title="${label}" data-instruction="${instruction}" data-success="${success}"><span class="sr-only">${label}: </span>${text}</button>`;
				from = index + text.length;
			}
			return out + part.slice(from);
		})
		.join("");
}

// An ability's whole rule. Its chances roll as the ability's own test.
// `rollable` off leaves the written chances as text, for an ability whose current chance the sheet already rolls.
export function RuleText({ html, name, rollable = true, class: className = "" }: { html: string; name: string; rollable?: boolean; class?: string }) {
	const roller = useContext(RollContext);
	const marked = useMemo(
		() => (roller && rollable ? chancify(html, (instruction, success) => roller.title({ instruction, success, description: name })) : html),
		[html, name, roller, rollable],
	);
	const element = useRef<HTMLDivElement>(null);
	useEffect(() => {
		const root = element.current;
		if (!root || !roller) return undefined;
		const onClick = (event: Event) => {
			const chip = (event.target as Element).closest<HTMLElement>("button.cs-chance");
			if (chip) void roller.roll({ instruction: chip.dataset.instruction ?? "", success: chip.dataset.success, description: name });
		};
		root.addEventListener("click", onClick);
		return () => root.removeEventListener("click", onClick);
	}, [roller, name]);
	return <div ref={element} class={className} dangerouslySetInnerHTML={{ __html: marked }} />;
}
